import React, { useState, useEffect, useRef } from "react";
import { CrudFilter, useList } from "@refinedev/core";
import {
  Box,
  Input,
  InputGroup,
  InputRightElement,
  FormControl,
  FormLabel,
  FormErrorMessage,
  List,
  ListItem,
  Text,
  Spinner,
  VStack,
  IconButton,
  useColorModeValue,
} from "@chakra-ui/react";
import { ChevronDown, ChevronUp, X } from "lucide-react";

interface Option {
  id: string | number;
  label: string;
  value: string;
}

interface AutoCompleteProps {
  source: string;
  label?: string;
  placeholder?: string;
  value?: string;
  onChange: (value: string) => void;
  displayField?: string;
  valueField?: string;
  filters?: CrudFilter[];
  debounceMs?: number;
  minChars?: number;
  maxResults?: number;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  className?: string;
  allowClear?: boolean;
  onClear?: () => void;
  sortBy?: {
    field: string;
    order: "asc" | "desc";
  };
}

export const AutoComplete: React.FC<AutoCompleteProps> = ({
  source,
  label,
  placeholder,
  value = "",
  onChange,
  displayField = "name",
  valueField = "id",
  filters = [],
  debounceMs = 300,
  minChars = 1,
  maxResults = 9999,
  disabled = false,
  required = false,
  error,
  className = "",
  allowClear = true,
  onClear,
  sortBy,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const didInit = useRef(false);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(
      () => setDebouncedSearch(searchTerm),
      debounceMs
    );
    return () => clearTimeout(handler);
  }, [searchTerm, debounceMs]);

  // Chakra UI colors
  const boxBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const highlightBg = useColorModeValue("blue.50", "blue.900");
  const highlightColor = useColorModeValue("blue.900", "blue.100");
  const textColor = useColorModeValue("gray.900", "white");
  const placeholderColor = useColorModeValue("gray.500", "gray.400");

  const mergedFilters: CrudFilter[] = [
    ...(Array.isArray(filters) ? filters : []),
  ];

  if (debouncedSearch) {
    mergedFilters.push({
      field: "search",
      operator: "eq",
      value: debouncedSearch,
    } as CrudFilter);
  }

  const { data, isLoading, isError } = useList({
    resource: source,
    filters: mergedFilters,
    sorters: sortBy ? [{ field: sortBy.field, order: sortBy.order }] : [],
    pagination: {
      pageSize: maxResults,
      current: 1,
    },
    queryOptions: {
      enabled:
        debouncedSearch.length >= minChars || debouncedSearch.length === 0,
    },
  });

  const getNestedValue = (obj: any, path: string): any => {
    return path.split(".").reduce((current, key) => current?.[key], obj);
  };

  // Map API data to options
  const options: Option[] =
    data?.data?.map((item: any) => ({
      id: item[valueField],
      label: getNestedValue(item, displayField),
      value: item[valueField].toString(),
    })) || [];

  // Sync selected option & searchTerm when value or options change
  useEffect(() => {
    if (!didInit.current && value) {
      const match = options.find((opt) => opt.value === value);
      if (match) {
        setSelectedOption(match);
        setSearchTerm(match.label);
        onChange(match.value); // ✅ only fire once for initial value
        didInit.current = true;
      }
    }
  }, [value, options, onChange]);

  // Input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  // Option select
  const handleOptionSelect = (option: Option) => {
    setSelectedOption(option);
    setSearchTerm(option.label);
    setIsOpen(false);
    setHighlightedIndex(-1);
    onChange(option.value);
  };

  // Clear selection
  const handleClear = () => {
    setSelectedOption(null);
    setSearchTerm("");
    setIsOpen(false);
    setHighlightedIndex(-1);
    onChange(null as any);
    onClear?.();
    inputRef.current?.focus();
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < options.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : options.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && options[highlightedIndex]) {
          handleOptionSelect(options[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Clear button condition
  const showClearButton = allowClear && searchTerm && !disabled;

  return (
    <Box ref={containerRef} position="relative" className={className}>
      <FormControl isInvalid={!!error} isRequired={required}>
        {label && (
          <FormLabel fontSize="sm" fontWeight="medium">
            {label}
          </FormLabel>
        )}

        <InputGroup pb={2}>
          <Input
            ref={inputRef}
            placeholder={placeholder}
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsOpen(true)}
            disabled={disabled}
            autoComplete="off"
            pr={showClearButton ? "4rem" : "2.5rem"}
          />
          <InputRightElement width={showClearButton ? "4rem" : "2.5rem"}>
            {showClearButton && (
              <IconButton
                aria-label="Clear selection"
                icon={<X size={14} />}
                size="xs"
                variant="ghost"
                colorScheme="gray"
                onClick={handleClear}
                mr={1}
                minW="auto"
                h="auto"
                p={1}
                _hover={{ bg: hoverBg }}
              />
            )}
            {isLoading ? (
              <Spinner size="sm" color="gray.500" />
            ) : isOpen ? (
              <ChevronUp size={16} color="#878787" />
            ) : (
              <ChevronDown size={16} color="#878787" />
            )}
          </InputRightElement>
        </InputGroup>

        {error && <FormErrorMessage>{error}</FormErrorMessage>}
      </FormControl>

      {isOpen && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={50}
          mt={1}
          bg={boxBg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="xl"
          boxShadow="lg"
          maxH="240px"
          overflowY="auto"
        >
          {isError ? (
            <Box px={4} py={3}>
              <Text fontSize="sm" color="red.500">
                Error loading options
              </Text>
            </Box>
          ) : isLoading ? (
            <Box px={4} py={3}>
              <VStack spacing={2}>
                <Spinner size="sm" />
                <Text fontSize="sm" color={placeholderColor}>
                  Loading...
                </Text>
              </VStack>
            </Box>
          ) : options.length === 0 ? (
            <Box px={4} py={3}>
              <Text fontSize="sm" color={placeholderColor}>
                {searchTerm.length < minChars
                  ? `Type at least ${minChars} character${
                      minChars > 1 ? "s" : ""
                    } to search`
                  : "No options found"}
              </Text>
            </Box>
          ) : (
            <List spacing={0} py={1}>
              {options.map((option, index) => (
                <ListItem
                  key={option.id}
                  px={4}
                  py={3}
                  cursor="pointer"
                  fontSize="sm"
                  color={
                    index === highlightedIndex ? highlightColor : textColor
                  }
                  bg={index === highlightedIndex ? highlightBg : "transparent"}
                  _hover={{ bg: hoverBg }}
                  onClick={() => handleOptionSelect(option)}
                >
                  {option.label}
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}
    </Box>
  );
};
